import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Calendar, MapPin, Users } from "lucide-react";
import { getProfileImageUrl } from "@/lib/profile-images";
import { getClubImageUrl } from "@/lib/club-images";
import { DISCIPLINAS, disciplinaEmoji, disciplinaLabel } from "@/lib/disciplinas";

export const Route = createFileRoute("/atleta/$username")({
  head: ({ params }) => ({
    meta: [
      { title: `@${params.username} — Atleta Brulo` },
      { name: "description", content: `Perfil público de @${params.username} en Brulo.` },
      { property: "og:title", content: `@${params.username} — Atleta Brulo` },
      { property: "og:description", content: `Perfil público de @${params.username} en Brulo.` },
    ],
  }),
  component: AthletePage,
});

type Athlete = {
  id: string;
  username: string;
  full_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  city: string | null;
  estado: string | null;
  disciplinas: string[] | null;
  created_at: string;
};

type ClubRow = { id: string; name: string; slug: string; type: string; city: string | null; logo_url: string | null; logoSigned?: string | null };

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  club_id: string | null;
  club_name: string | null;
  club_slug: string | null;
};

function memberSince(iso: string) {
  return new Date(iso).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
}

function AthletePage() {
  const { username } = Route.useParams();
  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: ar, error } = await supabase.rpc("get_public_athlete" as never, { _username: username } as never);
      const a = ((ar ?? []) as unknown as Athlete[])[0];
      if (error || !a) { setNotFound(true); setLoading(false); return; }
      setAthlete(a);
      setAvatarUrl(await getProfileImageUrl(a.avatar_url));

      const [{ data: cl }, { data: ev }] = await Promise.all([
        supabase.rpc("get_athlete_clubs" as never, { _user_id: a.id } as never),
        supabase.rpc("get_athlete_events" as never, { _user_id: a.id } as never),
      ]);
      const clList = ((cl ?? []) as unknown as ClubRow[]);
      const signed = await Promise.all(clList.map((c) => getClubImageUrl(c.logo_url)));
      setClubs(clList.map((c, i) => ({ ...c, logoSigned: signed[i] })));
      setEvents(((ev ?? []) as unknown as EventRow[]));
      setLoading(false);
    })();
  }, [username]);

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-20 text-muted-foreground">Cargando…</div>;
  }
  if (notFound || !athlete) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20">
        <h1 className="font-display text-3xl">Atleta no encontrado</h1>
        <Link to="/" className="text-primary hover:underline mt-4 inline-block">Volver al inicio</Link>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.event_date >= today);
  const past = events.filter((e) => e.event_date < today);

  return (
    <div className="pb-16">
      <div className="mx-auto max-w-4xl px-4 md:px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Inicio
        </Link>
      </div>

      <div className="mx-auto max-w-4xl px-4 md:px-6 pt-8 space-y-10">
        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-background bg-muted shadow-xl shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={athlete.full_name ?? athlete.username} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl font-display text-muted-foreground">
                {(athlete.full_name ?? athlete.username).charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="space-y-2 min-w-0">
            <p className="text-xs tracking-[0.3em] uppercase text-primary">atleta brulo</p>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight break-words">
              {athlete.full_name ?? `@${athlete.username}`}
            </h1>
            <p className="text-sm text-muted-foreground">@{athlete.username}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground pt-1">
              {(athlete.city || athlete.estado) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {[athlete.city, athlete.estado].filter(Boolean).join(", ")}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Miembro desde {memberSince(athlete.created_at)}
              </span>
            </div>
          </div>
        </header>

        {/* Bio */}
        {athlete.bio && (
          <section>
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed max-w-2xl whitespace-pre-line">
              {athlete.bio}
            </p>
          </section>
        )}

        {/* Disciplinas */}
        {athlete.disciplinas && athlete.disciplinas.length > 0 && (
          <section>
            <h2 className="font-display text-xl mb-3">Disciplinas</h2>
            <div className="flex flex-wrap gap-2">
              {athlete.disciplinas.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-primary text-sm font-semibold">
                  <span>{disciplinaEmoji(d)}</span> {disciplinaLabel(d)}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Clubs */}
        <section>
          <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Clubs
          </h2>
          {clubs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Este atleta aún no pertenece a ningún club.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clubs.map((c) => (
                <Link
                  key={c.id}
                  to="/club/$slug"
                  params={{ slug: c.slug }}
                  className="rounded-lg border border-border bg-card p-4 hover:border-primary/40 transition flex items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-muted shrink-0">
                    {c.logoSigned ? (
                      <img src={c.logoSigned} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-base font-display text-muted-foreground">
                        {c.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-base truncate">{c.name}</h3>
                    {c.city && <p className="text-xs text-muted-foreground truncate">{c.city}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Eventos */}
        <section>
          <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> Eventos
          </h2>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">Este atleta aún no está inscrito en eventos.</p>
          ) : (
            <div className="space-y-6">
              {upcoming.length > 0 && (
                <div>
                  <h3 className="font-display text-lg mb-2 text-primary">Próximos</h3>
                  <EventList events={upcoming} />
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <h3 className="font-display text-lg mb-2 text-muted-foreground">Anteriores</h3>
                  <EventList events={past} />
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function EventList({ events }: { events: EventRow[] }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {events.map((ev) => {
        const dateStr = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-MX", {
          weekday: "short", day: "numeric", month: "short",
        });
        return (
          <Link
            key={ev.id}
            to="/eventos/$eventId"
            params={{ eventId: ev.id }}
            className="block rounded-lg border border-border bg-background/40 p-4 hover:bg-background/60 hover:border-primary/40 transition"
          >
            {ev.club_name && (
              <p className="text-xs uppercase tracking-wider text-primary mb-1 truncate">{ev.club_name}</p>
            )}
            <h4 className="font-display text-base leading-snug mb-1">{ev.name}</h4>
            <p className="text-xs text-muted-foreground capitalize">{dateStr} · {ev.event_time?.slice(0, 5)}</p>
          </Link>
        );
      })}
    </div>
  );
}

// silence unused import in some build configs
void DISCIPLINAS;
